import React, { useEffect } from 'react';
import Layout from '@/components/Layout';
import Hero from '@/components/Hero';
import ServicesSection from '@/components/ServicesSection';
import BenefitsSection from '@/components/BenefitsSection';
import TestimonialsSection from '@/components/TestimonialsSection';
import FaqSection from '@/components/FaqSection';
import AboutMeSection from '@/components/AboutMeSection'; // ייבוא קומפוננטת אודות חדשה
import ContactForm from '@/components/ContactForm'; // ייבוא קומפוננטת טופס יצירת קשר
import SEO from '@/components/SEO';
import { generateFAQSchema, generateServiceSchema } from '@/components/SEO';

// FAQs לשימוש ב-Schema.org
const faqsForSchema = [
  {
    question: "מה כוללת פגישה ראשונית?",
    answer: "השיחה הראשונית כוללת הבנה של הבעיות הקיימות כיום בעסק ובדיקה ראשונית של פתרונות אפשריים. הפגישה נמשכת כרבע שעה בשיחת וידאו."
  },
  {
    question: "מה כוללות פגישות איפיון?",
    answer: "פגישות האפיון כוללות ניתוח מפורט של התהליכים בעסק, זיהוי תהליכים שניתן לייעל, ותכנון מפורט של פתרונות רלונטיים. מספר הפגישות הנדרשות, תלוי בהיקף התהליכים הנבחנים ומתקיימות בשיחת וידאו."
  },
  {
    question: "כמה זמן לוקח להטמיע מערכת חדשה?",
    answer: "זמן ההטמעה משתנה בהתאם למורכבות המערכת והיקף השימוש. הטמעת מערכות מדף יכולה להימשך בין שבוע לחודש, בעוד שפיתוח מערכת מותאמת אישית עשוי להימשך בין חודש לשלושה חודשים."
  },
  {
    question: "האם אתם מספקים תמיכה לאחר הטמעת המערכת?",
    answer: "בהחלט! אנו מציעים חבילות תמיכה שונות המותאמות לצרכי הלקוח, החל מתמיכה טכנית בסיסית ועד לליווי מלא הכולל עדכונים, שדרוגים והדרכות נוספות לצוות."
  },
  {
    question: "האם אתם עובדים עם עסקים קטנים או רק עם חברות גדולות?",
    answer: "אנחנו מתמחים במתן פתרונות לעסקים קטנים ובינוניים, ומתאימים את השירותים והתקציב בהתאם לגודל העסק וצרכיו הספציפיים."
  }
];

// שירותים לשימוש ב-Schema.org
const servicesForSchema = [
  {
    name: "ייעוץ חד פעמי",
    serviceType: "ייעוץ טכנולוגי",
    description: "פגישת ייעוץ ממוקדת לפתרון בעיות ספציפיות או לייעול תהליכים קיימים בעסק שלך.",
    provider: " שלומית ז'בנר",
    areaServed: "ישראל"
  },
  {
    name: "הטמעת מערכות מדף",
    serviceType: "שירותי יישום תוכנה",
    description: "בחירה והטמעה של פתרונות מוכנים המותאמים לצרכי העסק שלך, חיסכון בזמן ובמשאבים.",
    provider: " שלומית ז'בנר",
    areaServed: "ישראל"
  },
  {
    name: "פיתוח מערכות מותאמות אישית",
    serviceType: "פיתוח תוכנה",
    description: "פיתוח פתרונות טכנולוגיים ייחודיים המותאמים בדיוק לצרכים ולתהליכים הספציפיים בעסק שלך.",
    provider: " שלומית ז'בנר",
    areaServed: "ישראל"
  },
  {
    name: "סדנאות טכנולוגיה לעסקים",
    serviceType: "הדרכה והכשרה",
    description: "הדרכות וסדנאות מעשיות להכרת כלים טכנולוגיים והטמעתם בתהליכי העבודה היומיומיים.",
    provider: " שלומית ז'בנר",
    areaServed: "ישראל"
  }
];

// יצירת schema.org מורכב המשלב FAQ ושירותים
const createCombinedSchema = () => {
  return {
    "@context": "https://schema.org",
    "@graph": [
      generateFAQSchema(faqsForSchema),
      ...servicesForSchema.map(service => generateServiceSchema(service)),
      {
        "@type": "WebSite",
        "name": " שלומית ז'בנר",
        "url": "https://www.yourwebsite.com",
        "description": "פתרונות טכנולוגיים לעסקים קטנים ובינוניים. חוסכים לך זמן, כסף וכאבי ראש.",
        "inLanguage": "he-IL"
      },
      {
        "@type": "Organization",
        "name": " שלומית ז'בנר",
        "url": "https://www.yourwebsite.com",
        "logo": "https://www.yourwebsite.com/logo.png",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "054-4728562",
          "contactType": "customer service",
          "availableLanguage": ["Hebrew"]
        },
        "sameAs": [
          "https://www.facebook.com/your-facebook-page",
          "https://www.linkedin.com/company/your-linkedin-page"
        ]
      }
    ]
  };
};

const Index = () => {
  // Smooth scroll for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        e.preventDefault();
        const id = target.getAttribute('href')?.substring(1);
        if (id) {
          const element = document.getElementById(id);
          if (element) {
            window.scrollTo({
              top: element.offsetTop - 100,
              behavior: 'smooth',
            });
          }
        }
      }
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  return (
    <>
      <SEO 
        title=" שלומית ז'בנר | פתרונות טכנולוגיים לעסקים קטנים ובינוניים"
        description="אנחנו מספקים פתרונות טכנולוגיים לעסקים קטנים ובינוניים. חוסכים לך זמן, כסף וכאבי ראש באמצעות פתרונות דיגיטליים מותאמים אישית."
        keywords="פתרונות טכנולוגיים, עסקים קטנים, טכנולוגיה לעסקים, אוטומציה לעסקים, ייעול תהליכים, הטמעת מערכות, תמיכה טכנית, דיגיטציה"
        canonicalUrl="/"
        structuredData={createCombinedSchema()}
      />
      <Layout>
        <div className="sr-only" id="main-content">תוכן עיקרי</div>
        <main id="main-content" role="main" aria-label="תוכן עיקרי">
          <Hero />
          <ServicesSection />
          <BenefitsSection />
          <AboutMeSection /> {/* הוספת סקציית אודות */}
          <TestimonialsSection />
          <FaqSection />
          <ContactForm formId="bkWBCuB93rus" />
        </main>
      </Layout>
    </>
  );
};

export default Index;